/**
 * UTM + attribution capture.
 *
 * Persists marketing attribution under a single localStorage key
 * (`vb_attribution`) so that downstream features (analytics, contact form,
 * etc.) can read a stable shape regardless of which agent is calling.
 *
 * Design notes:
 *  - SSR-safe: every storage access is guarded; calling these in a Node
 *    context (prerender, SSR, tests) returns sensible defaults instead of
 *    throwing.
 *  - First-touch is sticky — once set, it never gets overwritten. Last-touch
 *    is updated on every `captureUTM()` call where any UTM is present.
 *  - Storage failures (private mode, quota, disabled cookies) are swallowed.
 *    Attribution is best-effort, never load-bearing.
 *
 * Storage contract (do not change without coordinating with consumers):
 *   key:    `vb_attribution`
 *   value:  JSON-stringified `Attribution` object
 */

export interface Attribution {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  referrer?: string;
  /** ISO timestamp of the very first UTM-bearing visit. Sticky. */
  firstTouch?: string;
  /** ISO timestamp of the most recent UTM-bearing visit. */
  lastTouch?: string;
  /** `window.location.pathname` of the first session entry. */
  landingPath?: string;
}

const STORAGE_KEY = "vb_attribution";

/** Minimal Storage shape — works for both real localStorage and our no-op. */
interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const NOOP_STORAGE: SafeStorage = {
  getItem: () => null,
  setItem: () => {
    /* noop */
  },
  removeItem: () => {
    /* noop */
  },
};

/**
 * Returns a localStorage-shaped object that never throws.
 * Falls back to an in-memory no-op in SSR or when storage is blocked
 * (private mode, disabled cookies, quota exceeded).
 */
function getStorage(): SafeStorage {
  if (typeof window === "undefined") return NOOP_STORAGE;
  try {
    const ls = window.localStorage;
    // Probe — Safari private mode throws on first write.
    const probeKey = "__vb_probe__";
    ls.setItem(probeKey, "1");
    ls.removeItem(probeKey);
    return ls;
  } catch {
    return NOOP_STORAGE;
  }
}

function readStored(): Attribution {
  const raw = getStorage().getItem(STORAGE_KEY);
  if (raw == null) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Attribution;
    }
    return {};
  } catch {
    return {};
  }
}

function writeStored(value: Attribution): void {
  try {
    getStorage().setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore — best-effort persistence
  }
}

function readParam(params: URLSearchParams, key: string): string | undefined {
  const v = params.get(key);
  if (v == null) return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Capture UTM parameters and referrer from the current URL into localStorage.
 * Idempotent — safe to call multiple times. Should be invoked once on app
 * boot (see `main.tsx`).
 *
 * Behaviour:
 *  - If any of `utm_source`, `utm_medium`, `utm_campaign`, `utm_term`,
 *    `utm_content` is present, write last-touch + UTM fields.
 *  - first-touch / referrer / landingPath are written only if not already
 *    set in storage (sticky).
 *  - In SSR or when storage is unavailable: returns silently.
 */
export function captureUTM(): void {
  if (typeof window === "undefined") return;

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  const incoming: Pick<
    Attribution,
    "source" | "medium" | "campaign" | "term" | "content"
  > = {
    source: readParam(params, "utm_source"),
    medium: readParam(params, "utm_medium"),
    campaign: readParam(params, "utm_campaign"),
    term: readParam(params, "utm_term"),
    content: readParam(params, "utm_content"),
  };

  const hasAnyUtm =
    incoming.source !== undefined ||
    incoming.medium !== undefined ||
    incoming.campaign !== undefined ||
    incoming.term !== undefined ||
    incoming.content !== undefined;

  const existing = readStored();
  const now = new Date().toISOString();

  // Sticky fields — only set on the very first capture.
  const referrer =
    existing.referrer ??
    (typeof document !== "undefined" && document.referrer
      ? document.referrer
      : undefined);
  const landingPath =
    existing.landingPath ??
    (typeof window.location.pathname === "string"
      ? window.location.pathname
      : undefined);
  const firstTouch = existing.firstTouch ?? (hasAnyUtm ? now : undefined);

  // If no UTM is present and we already have stored data, still refresh
  // sticky-only fields (referrer / landingPath) on first visit.
  if (!hasAnyUtm) {
    if (existing.referrer === referrer && existing.landingPath === landingPath) {
      return;
    }
    writeStored({
      ...existing,
      referrer,
      landingPath,
    });
    return;
  }

  const next: Attribution = {
    source: incoming.source ?? existing.source,
    medium: incoming.medium ?? existing.medium,
    campaign: incoming.campaign ?? existing.campaign,
    term: incoming.term ?? existing.term,
    content: incoming.content ?? existing.content,
    referrer,
    firstTouch,
    lastTouch: now,
    landingPath,
  };

  writeStored(next);
}

/**
 * Read the persisted attribution. Returns `{}` if nothing has been captured
 * or storage is unavailable (SSR, private mode, etc.).
 */
export function getAttribution(): Attribution {
  return readStored();
}

/**
 * Remove the persisted attribution. Provided primarily for tests and
 * consent/privacy reset flows.
 */
export function clearAttribution(): void {
  try {
    getStorage().removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Exposed for consumers that need the raw key (e.g. cross-tab sync). */
export const ATTRIBUTION_STORAGE_KEY = STORAGE_KEY;
